import { formatDistanceToNow, format, isValid } from 'date-fns';

type DateValue = string | Date | null | undefined;

/**
 * Parses API date values for display without shifting calendar dates across
 * time zones. JavaScript treats a bare `YYYY-MM-DD` value as UTC, which can
 * otherwise show the previous day for users west of UTC.
 */
export const parseDateValue = (value: DateValue): Date | undefined => {
  if (!value) return undefined;
  if (value instanceof Date) return isValid(value) ? value : undefined;

  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (dateOnly) {
    const [, year, month, day] = dateOnly;
    const parsed = new Date(Number(year), Number(month) - 1, Number(day));
    return isValid(parsed)
      && parsed.getFullYear() === Number(year)
      && parsed.getMonth() === Number(month) - 1
      && parsed.getDate() === Number(day)
      ? parsed
      : undefined;
  }

  // Python/FastAPI commonly serializes timestamps with a space separator and
  // six-digit microseconds. Normalize that valid backend representation into
  // browser-standard ISO before parsing.
  const normalized = value
    .replace(/^((?:\d{4}-\d{2}-\d{2}))\s/, '$1T')
    .replace(/\.(\d{3})\d+(?=(?:Z|[+-]\d{2}:?\d{2})?$)/, '.$1');
  const parsed = new Date(normalized);
  return isValid(parsed) ? parsed : undefined;
};

const normalizeConfidencePercent = (confidence: number | undefined): number | undefined => {
  if (confidence === undefined || confidence === null || Number.isNaN(confidence)) {
    return undefined;
  }
  if (confidence <= 1) {
    return confidence * 100;
  }
  return confidence;
};

export const formatCurrency = (amount: number, currency: string = 'USD'): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount);
};

export const formatNumber = (num: number, decimals: number = 2): string => {
  return Number.isInteger(num) ? num.toString() : num.toFixed(decimals);
};

export const formatConfidence = (confidence: number | undefined): string => {
  const normalizedConfidence = normalizeConfidencePercent(confidence);
  if (!normalizedConfidence) return 'N/A';
  return `${Math.round(normalizedConfidence)}%`;
};

export const formatDate = (date: DateValue): string => {
  const dateObj = parseDateValue(date);
  return dateObj ? format(dateObj, 'MMM d, yyyy') : '—';
};

export const formatDateTime = (date: DateValue): string => {
  const dateObj = parseDateValue(date);
  return dateObj ? format(dateObj, 'MMM d, yyyy, h:mm a') : '—';
};

export const formatShortDate = (date: DateValue): string => {
  const dateObj = parseDateValue(date);
  return dateObj ? format(dateObj, 'MMM d') : '—';
};

export const formatTime = (date: DateValue): string => {
  const dateObj = parseDateValue(date);
  return dateObj ? format(dateObj, 'h:mm a') : '—';
};

export const formatRelativeTime = (date: DateValue): string => {
  const dateObj = parseDateValue(date);
  return dateObj ? formatDistanceToNow(dateObj, { addSuffix: true }) : '—';
};

export const getConfidenceColor = (confidence: number | undefined): string => {
  const normalizedConfidence = normalizeConfidencePercent(confidence);
  if (!normalizedConfidence) return 'gray';
  if (normalizedConfidence >= 90) return 'green';
  if (normalizedConfidence >= 70) return 'yellow';
  return 'red';
};

export const getStatusColor = (status: string): string => {
  switch (status.toLowerCase()) {
    case 'approved':
    case 'matched':
    case 'success':
      return 'green';
    case 'pending_review':
    case 'pending':
      return 'yellow';
    case 'rejected':
    case 'failed':
      return 'red';
    default:
      return 'gray';
  }
};

export const truncate = (str: string, length: number = 50): string => {
  return str.length > length ? `${str.substring(0, length)}...` : str;
};
